import { useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';

export interface Marcador {
  id: string;
  numero: string;
  /** Ponta da seta: o componente apontado, em % da imagem. */
  x: number;
  y: number;
  /** Onde fica o número. Ausente = número em cima do ponto, sem seta. */
  lx?: number;
  ly?: number;
}

export interface Posicao { x: number; y: number }

const limitar = (valor: number) => Math.max(0, Math.min(100, valor));
/** Abaixo disto o arrasto conta como clique: número sobre o ponto, sem seta. */
const ARRASTO_MINIMO = 2;

/** Posição do ponteiro em % da imagem, a partir do quadro que a envolve. */
function posicaoRelativa(quadro: HTMLElement, clientX: number, clientY: number): Posicao | null {
  const area = quadro.getBoundingClientRect();
  if (!area.width || !area.height) return null;
  return {
    x: Number(limitar(((clientX - area.left) / area.width) * 100).toFixed(2)),
    y: Number(limitar(((clientY - area.top) / area.height) * 100).toFixed(2)),
  };
}

export function temSeta(marcador: Pick<Marcador, 'x' | 'y' | 'lx' | 'ly'>): boolean {
  return typeof marcador.lx === 'number' && typeof marcador.ly === 'number'
    && Math.hypot(marcador.lx - marcador.x, marcador.ly - marcador.y) >= ARRASTO_MINIMO;
}

/** Setas dos marcadores, em % — o mesmo SVG serve ao editor e à folha. */
export function SetasMarcadores({ marcadores, idPrefixo, cor = 'var(--manual-blue)', espessura = 1.5 }: {
  marcadores: Pick<Marcador, 'x' | 'y' | 'lx' | 'ly'>[];
  idPrefixo: string;
  cor?: string;
  espessura?: number;
}) {
  const setas = marcadores.filter(temSeta);
  if (!setas.length) return null;
  const marcador = `${idPrefixo}-ponta`;
  return (
    <svg className="manual-marcador-setas" aria-hidden="true">
      <defs>
        <marker id={marcador} markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto" markerUnits="userSpaceOnUse">
          <path d="M0,0 L8,4 L0,8 z" fill={cor} />
        </marker>
      </defs>
      {setas.map((seta, indice) => (
        <line
          key={indice}
          x1={`${seta.lx}%`}
          y1={`${seta.ly}%`}
          x2={`${seta.x}%`}
          y2={`${seta.y}%`}
          stroke={cor}
          strokeWidth={espessura}
          markerEnd={`url(#${marcador})`}
        />
      ))}
    </svg>
  );
}

type Arrasto =
  | { tipo: 'criar'; origem: Posicao; atual: Posicao }
  | { tipo: 'numero' | 'ponta'; id: string };

/**
 * Imagem com pontos numerados sobrepostos. Pressionar sobre o componente e
 * arrastar cria um marcador com seta: a ponta fica onde se pressionou, o
 * número onde se soltou. Um clique sem arrasto cria o número em cima do ponto.
 * Depois, arrastar o número ou a ponta reposiciona cada um. Tudo em % da
 * imagem, então vale igual na folha impressa, em qualquer escala.
 */
export function FiguraMarcada({ imagem, alt, marcadores, ativo, alturaMaxima, aoCriar, aoMover, aoSelecionar }: {
  imagem: string;
  alt: string;
  marcadores: Marcador[];
  ativo?: string | null;
  /** Teto da imagem em px: o quadro acompanha a imagem, e não o contrário. */
  alturaMaxima?: number;
  aoCriar: (marcador: { x: number; y: number; lx?: number; ly?: number }) => void;
  aoMover: (id: string, patch: Partial<Pick<Marcador, 'x' | 'y' | 'lx' | 'ly'>>) => void;
  aoSelecionar?: (id: string) => void;
}) {
  const quadro = useRef<HTMLDivElement>(null);
  const [arrasto, setArrasto] = useState<Arrasto | null>(null);

  const posicao = (evento: ReactPointerEvent) => (quadro.current ? posicaoRelativa(quadro.current, evento.clientX, evento.clientY) : null);

  const iniciar = (evento: ReactPointerEvent<HTMLElement>, proximo: Arrasto) => {
    evento.stopPropagation();
    evento.preventDefault();
    evento.currentTarget.setPointerCapture(evento.pointerId);
    setArrasto(proximo);
    if (proximo.tipo !== 'criar') aoSelecionar?.(proximo.id);
  };

  const mover = (evento: ReactPointerEvent<HTMLElement>) => {
    if (!arrasto) return;
    const atual = posicao(evento);
    if (!atual) return;
    if (arrasto.tipo === 'criar') setArrasto({ ...arrasto, atual });
    else if (arrasto.tipo === 'numero') aoMover(arrasto.id, { lx: atual.x, ly: atual.y });
    else aoMover(arrasto.id, { x: atual.x, y: atual.y });
  };

  const soltar = (evento: ReactPointerEvent<HTMLElement>) => {
    if (!arrasto) return;
    evento.currentTarget.releasePointerCapture(evento.pointerId);
    if (arrasto.tipo === 'criar') {
      const { origem, atual } = arrasto;
      aoCriar(temSeta({ ...origem, lx: atual.x, ly: atual.y }) ? { ...origem, lx: atual.x, ly: atual.y } : origem);
    }
    setArrasto(null);
  };

  const previa = arrasto?.tipo === 'criar' ? arrasto : null;

  return (
    <div
      ref={quadro}
      className="manual-marcador-quadro"
      onPointerDown={(evento) => {
        const origem = posicao(evento);
        if (origem) iniciar(evento, { tipo: 'criar', origem, atual: origem });
      }}
      onPointerMove={mover}
      onPointerUp={soltar}
      onPointerCancel={soltar}
    >
      <img src={imagem} alt={alt} draggable={false} style={alturaMaxima ? { maxHeight: alturaMaxima } : undefined} />
      <SetasMarcadores
        idPrefixo="editor"
        marcadores={previa ? [...marcadores, { ...previa.origem, lx: previa.atual.x, ly: previa.atual.y }] : marcadores}
      />
      {marcadores.map((marcador) => {
        const comSeta = temSeta(marcador);
        const numeroEm = comSeta ? { x: marcador.lx as number, y: marcador.ly as number } : marcador;
        return (
          <span key={marcador.id}>
            {comSeta && (
              <button
                type="button"
                className="manual-marcador-ponta"
                style={{ left: `${marcador.x}%`, top: `${marcador.y}%` }}
                title={`Ponta da seta ${marcador.numero} — arraste para mudar o componente apontado`}
                aria-label={`Ponta da seta ${marcador.numero}`}
                onPointerDown={(evento) => iniciar(evento, { tipo: 'ponta', id: marcador.id })}
                onPointerMove={mover}
                onPointerUp={soltar}
                onPointerCancel={soltar}
              />
            )}
            <button
              type="button"
              className={`manual-marcador-ponto ${ativo === marcador.id ? 'is-ativo' : ''}`}
              style={{ left: `${numeroEm.x}%`, top: `${numeroEm.y}%` }}
              title={`Ponto ${marcador.numero} — arraste para reposicionar o número`}
              aria-label={`Ponto ${marcador.numero}`}
              onPointerDown={(evento) => iniciar(evento, { tipo: 'numero', id: marcador.id })}
              onPointerMove={mover}
              onPointerUp={soltar}
              onPointerCancel={soltar}
            >
              {marcador.numero || '?'}
            </button>
          </span>
        );
      })}
      {previa && (
        <span className="manual-marcador-ponto is-previa" style={{ left: `${previa.atual.x}%`, top: `${previa.atual.y}%` }} />
      )}
    </div>
  );
}

/** Próximo número livre entre os já usados ("1", "2", "4" → "3"). */
export function proximoNumero(usados: string[]): string {
  const ocupados = new Set(usados.map((valor) => Number(valor.trim())).filter((valor) => Number.isInteger(valor) && valor > 0));
  let numero = 1;
  while (ocupados.has(numero)) numero += 1;
  return String(numero);
}
